import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { defineMessages, useIntl } from 'react-intl';
import AppNav from '../AppNav/AppNav';
import JobTitleField from '../JobTitleField/JobTitleField';
import {
  formatMatterReferenceMessage,
  formatTokenMessage,
  sharedMessages,
} from '../../utils/uiMessages';

const initialForm = {
  matter_reference_code: '',
  matter_title: '',
  matter_path: '',
  title: '',
  job_kind: '',
  office_action_path: '',
  status: '',
  incoming_date: '',
  internal_due_date: '',
  legal_due_date: '',
  client_due_date: '',
  legal_due_buffer_days: 2,
  estimated_hours: 1,
  remaining_hours: '',
  priority: '',
  notes: '',
};

const JOB_KIND_PRESETS = [
  {
    title: 'Report OA to client',
    preferredJobKinds: ['Reporting to the client', 'OA reporting'],
  },
  {
    title: 'Obtain client instructions',
    preferredJobKinds: ['Client communication', 'Others'],
  },
  {
    title: 'Answer client questions',
    preferredJobKinds: ['Client communication', 'Others'],
  },
  {
    title: 'Prepare response to the PTO',
    preferredJobKinds: ['Prepare submission to the PTO', 'Prepare response to the PTO'],
  },
  {
    title: 'File response with the PTO',
    preferredJobKinds: ['Prepare submission to the PTO', 'File response with the PTO'],
  },
  {
    title: 'Application drafting',
    preferredJobKinds: ['Others', 'Application drafting'],
  },
  {
    title: 'OA reporting',
    preferredJobKinds: ['Reporting to the client', 'OA reporting'],
  },
  {
    title: 'Client communication',
    preferredJobKinds: ['Client communication', 'Others'],
  },
  {
    title: 'Research / analysis',
    preferredJobKinds: ['Others', 'Research / analysis'],
  },
];

const messages = defineMessages({
  autoClassification: {
    id: 'Classification is set automatically to {jobKind}.',
    defaultMessage: 'Classification is set automatically to {jobKind}.',
  },
  overrideClassification: {
    id: 'Override classification',
    defaultMessage: 'Override classification',
  },
  jobDetails: {
    id: 'Job details',
    defaultMessage: 'Job details',
  },
  jobKindSettingsFailed: {
    id: 'Job kind settings failed with status {status}',
    defaultMessage: 'Job kind settings failed with status {status}',
  },
  jobLookupFailed: {
    id: 'Job lookup failed with status {status}',
    defaultMessage: 'Job lookup failed with status {status}',
  },
  jobUpdated: {
    id: 'Job updated.',
    defaultMessage: 'Job updated.',
  },
  createFollowUpJob: {
    id: 'Create associated job',
    defaultMessage: 'Create associated job',
  },
  loadingJob: {
    id: 'Loading job…',
    defaultMessage: 'Loading job…',
  },
  noJobPathProvided: {
    id: 'No job path was provided.',
    defaultMessage: 'No job path was provided.',
  },
  requestFailedWithStatus: {
    id: 'Request failed with status {status}',
    defaultMessage: 'Request failed with status {status}',
  },
  reviewAndUpdateJob: {
    id: 'Review and update status, deadlines, and workload fields here.',
    defaultMessage: 'Review and update status, deadlines, and workload fields here.',
  },
  saving: {
    id: 'Saving…',
    defaultMessage: 'Saving…',
  },
});

export default function JobEditorPage() {
  const intl = useIntl();
  const token = useSelector((state) => state.userSession?.token);
  const jobPath = useMemo(() => {
    if (typeof window === 'undefined') {
      return '';
    }
    const params = new URLSearchParams(window.location.search);
    return normalizeContentPath(params.get('path') || '');
  }, []);

  const [form, setForm] = useState(initialForm);
  const [original, setOriginal] = useState(initialForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [jobKindOptions, setJobKindOptions] = useState([]);
  const [showJobKindOverride, setShowJobKindOverride] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  function authHeaders(extra = {}) {
    return token
      ? {
          ...extra,
          Authorization: `Bearer ${token}`,
        }
      : extra;
  }

  useEffect(() => {
    let active = true;

    async function loadJobKinds() {
      try {
        const response = await fetch('/++api++/@job-kind-settings', {
          headers: authHeaders({ Accept: 'application/json' }),
        });
        if (!response.ok) {
          throw new Error(
            intl.formatMessage(messages.jobKindSettingsFailed, {
              status: response.status,
            }),
          );
        }
        const payload = await response.json();
        if (active) {
          setJobKindOptions(payload.items || []);
        }
      } catch {
        if (active) {
          setJobKindOptions([]);
        }
      }
    }

    loadJobKinds();
    return () => {
      active = false;
    };
  }, [intl, token]);

  useEffect(() => {
    let active = true;

    async function load() {
      if (!jobPath) {
        setError(intl.formatMessage(messages.noJobPathProvided));
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`/++api++${jobPath}`, {
          headers: authHeaders({ Accept: 'application/json' }),
        });
        if (!response.ok) {
          throw new Error(
            intl.formatMessage(messages.jobLookupFailed, {
              status: response.status,
            }),
          );
        }
        const payload = await response.json();
        let normalized = normalizeJob(payload);
        const matterPath =
          normalized.matter_path || normalizeContentPath(payload.parent?.['@id'] || '');

        if (matterPath && (!normalized.matter_reference_code || !normalized.matter_title)) {
          const matterResponse = await fetch(`/++api++${matterPath}`, {
            headers: authHeaders({ Accept: 'application/json' }),
          });
          if (matterResponse.ok) {
            const matterPayload = await matterResponse.json();
            normalized = {
              ...normalized,
              matter_reference_code:
                matterPayload.matter_reference_code ||
                matterPayload.matter_reference ||
                '',
              matter_title: matterPayload.title || normalized.matter_title,
            };
          }
        }
        if (active) {
          setForm(normalized);
          setOriginal(normalized);
        }
      } catch (loadError) {
        if (active) {
          setError(loadError.message);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      active = false;
    };
  }, [intl, jobPath, token]);

  const autoJobKind = useMemo(
    () => resolveTitleDrivenJobKind(form.title, jobKindOptions),
    [form.title, jobKindOptions],
  );
  const classificationMatchesAuto =
    autoJobKind &&
    normalizeJobKindLabel(form.job_kind) === normalizeJobKindLabel(autoJobKind);
  const showJobKindField =
    showJobKindOverride || !autoJobKind || Boolean(form.job_kind && !classificationMatchesAuto);

  useEffect(() => {
    if (!autoJobKind || showJobKindOverride || showJobKindField) {
      return;
    }
    setForm((current) =>
      current.job_kind === autoJobKind ? current : { ...current, job_kind: autoJobKind },
    );
  }, [autoJobKind, showJobKindField, showJobKindOverride]);

  async function onSave(event) {
    event.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const normalizedBufferDays = Number(form.legal_due_buffer_days);
      const normalizedEstimatedHours = String(form.estimated_hours ?? '').trim();
      const normalizedRemainingHours = String(form.remaining_hours ?? '').trim();
      const requestPayload = {
        title: form.title,
        job_kind: form.job_kind,
        status: form.status,
        incoming_date: form.incoming_date || null,
        internal_due_date: form.internal_due_date || null,
        legal_due_date: form.legal_due_date || null,
        client_due_date: form.client_due_date || null,
        legal_due_buffer_days: Number.isNaN(normalizedBufferDays)
          ? 0
          : normalizedBufferDays,
        estimated_hours: normalizedEstimatedHours || '0',
        remaining_hours: normalizedRemainingHours || null,
        priority: form.priority,
        notes: form.notes,
      };
      const response = await fetch(`/++api++${jobPath}`, {
        method: 'PATCH',
        headers: authHeaders({
          Accept: 'application/json',
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify(requestPayload),
      });
      if (!response.ok) {
        throw new Error(await extractError(response, intl));
      }
      let normalized;
      if (response.status === 204) {
        normalized = normalizeJob({
          ...original,
          ...requestPayload,
          matter_reference_code: form.matter_reference_code,
          matter_title: form.matter_title,
          matter_path: form.matter_path,
          office_action_path: form.office_action_path,
        });
      } else {
        const payload = await response.json();
        normalized = normalizeJob(payload);
      }
      setForm(normalized);
      setOriginal(normalized);
      setSuccess(intl.formatMessage(messages.jobUpdated));
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setSaving(false);
    }
  }

  function onCancel() {
    setForm(original);
    setShowJobKindOverride(false);
    setError('');
    setSuccess('');
  }

  function onToggleJobKindOverride() {
    setShowJobKindOverride((current) => {
      const next = !current;
      if (!next && autoJobKind) {
        setForm((formState) => ({ ...formState, job_kind: autoJobKind }));
      }
      return next;
    });
  }

  return (
    <main className="patent-entry">
      <section className="patent-entry__shell">
        <AppNav currentPath="/jobs" />
        <header className="patent-entry__hero">
          <p className="patent-entry__eyebrow">
            {intl.formatMessage(sharedMessages.jobs)}
          </p>
          {form.matter_reference_code ? (
            <p className="patent-entry__context-chip">
              {intl.formatMessage(sharedMessages.matterRef)}:{' '}
              {formatMatterReferenceMessage(intl, form.matter_reference_code)}
            </p>
          ) : null}
          <h1>{form.title || intl.formatMessage(messages.jobDetails)}</h1>
          <p className="patent-entry__note">
            {intl.formatMessage(messages.reviewAndUpdateJob)}
          </p>
          <div className="patent-entry__actions patent-entry__actions--row">
            {isOAWorkspaceCandidate(form) ? (
              <Link
                className="patent-entry__submit patent-entry__submit--link"
                to={`/oa-workspace?path=${encodeURIComponent(jobPath)}`}
              >
                {intl.formatMessage(sharedMessages.openOaWorkspace)}
              </Link>
            ) : null}
            {form.matter_path ? (
              <Link
                className="patent-entry__ghost"
                to={`/register-job?matter_path=${encodeURIComponent(
                  form.matter_path,
                )}&source_job_path=${encodeURIComponent(jobPath)}`}
              >
                {intl.formatMessage(messages.createFollowUpJob)}
              </Link>
            ) : null}
            <Link className="patent-entry__ghost" to="/overview">
              {intl.formatMessage(sharedMessages.backToOverview)}
            </Link>
          </div>
        </header>

        {loading ? (
          <p className="patent-entry__note">
            {intl.formatMessage(messages.loadingJob)}
          </p>
        ) : null}
        {error && !loading ? <p className="patent-entry__error">{error}</p> : null}

        {!loading && !error ? (
          <form className="patent-entry__form" onSubmit={onSave}>
            <TextField
              label={intl.formatMessage(sharedMessages.matterReferenceCode)}
              value={form.matter_reference_code}
              disabled
              onChange={() => {}}
            />
            <TextField
              label={intl.formatMessage(sharedMessages.matterTitle)}
              value={form.matter_title}
              disabled
              onChange={() => {}}
            />
            <JobTitleField
              value={form.title}
              onChange={(value) => setForm((current) => ({ ...current, title: value }))}
              options={jobKindOptions}
              required
            />
            {autoJobKind && !showJobKindField ? (
              <label className="patent-entry__field">
                <span className="patent-entry__label">
                  {intl.formatMessage(sharedMessages.jobKind)}
                </span>
                <span className="patent-entry__help">
                  {intl.formatMessage(messages.autoClassification, {
                    jobKind: formatTokenMessage(intl, autoJobKind),
                  })}
                </span>
                <button
                  className="patent-entry__ghost"
                  type="button"
                  onClick={onToggleJobKindOverride}
                >
                  {intl.formatMessage(messages.overrideClassification)}
                </button>
              </label>
            ) : null}
            {showJobKindField ? (
              <SelectField
                label={intl.formatMessage(sharedMessages.jobKind)}
                value={form.job_kind}
                onChange={(value) => setForm((current) => ({ ...current, job_kind: value }))}
                options={mergeLegacyOption(jobKindOptions, form.job_kind)}
              />
            ) : null}
            <SelectField
              label={intl.formatMessage(sharedMessages.status)}
              value={form.status}
              onChange={(value) => setForm((current) => ({ ...current, status: value }))}
              options={[
                'triage',
                'not_started',
                'in_progress',
                'queued_for_review',
                'waiting_on_client',
                'waiting_on_foreign_associate',
                'ready_to_file',
                'blocked',
                'done',
                'cancelled',
                ]}
            />
            <div className="patent-entry__date-stack patent-entry__field--wide">
              <DateField
                label={intl.formatMessage(sharedMessages.incomingDate)}
                value={form.incoming_date}
                onChange={(value) =>
                  setForm((current) => ({ ...current, incoming_date: value }))
                }
              />
              <DateField
                label={intl.formatMessage(sharedMessages.internalDueDate)}
                value={form.internal_due_date}
                onChange={(value) =>
                  setForm((current) => ({ ...current, internal_due_date: value }))
                }
              />
              <DateField
                label={intl.formatMessage(sharedMessages.legalDueDate)}
                value={form.legal_due_date}
                onChange={(value) =>
                  setForm((current) => ({ ...current, legal_due_date: value }))
                }
              />
              <DateField
                label={intl.formatMessage(sharedMessages.clientDueDate)}
                value={form.client_due_date}
                onChange={(value) =>
                  setForm((current) => ({ ...current, client_due_date: value }))
                }
              />
            </div>
            <NumberField
              label={intl.formatMessage(sharedMessages.businessDayBuffer)}
              value={form.legal_due_buffer_days}
              min="0"
              onChange={(value) =>
                setForm((current) => ({ ...current, legal_due_buffer_days: value }))
              }
            />
            <NumberField
              label={intl.formatMessage(sharedMessages.estimatedHours)}
              value={form.estimated_hours}
              min="0"
              step="0.5"
              onChange={(value) =>
                setForm((current) => ({ ...current, estimated_hours: value }))
              }
            />
            <NumberField
              label={intl.formatMessage(sharedMessages.remainingHours)}
              value={form.remaining_hours}
              min="0"
              step="0.5"
              onChange={(value) =>
                setForm((current) => ({ ...current, remaining_hours: value }))
              }
            />
            <SelectField
              label={intl.formatMessage(sharedMessages.priority)}
              value={form.priority}
              onChange={(value) =>
                setForm((current) => ({ ...current, priority: value }))
              }
              options={['low', 'medium', 'high', 'critical']}
            />
            <TextAreaField
              label={intl.formatMessage(sharedMessages.notes)}
              value={form.notes}
              onChange={(value) => setForm((current) => ({ ...current, notes: value }))}
            />

            {success ? <p className="patent-entry__success">{success}</p> : null}

            <div className="patent-entry__actions patent-entry__actions--row">
              <button className="patent-entry__submit" type="submit" disabled={saving}>
                {saving
                  ? intl.formatMessage(messages.saving)
                  : intl.formatMessage(sharedMessages.save)}
              </button>
              <button
                className="patent-entry__ghost"
                type="button"
                onClick={onCancel}
                disabled={saving}
              >
                {intl.formatMessage(sharedMessages.cancelChanges)}
              </button>
              <Link className="patent-entry__ghost" to="/overview">
                {intl.formatMessage(sharedMessages.backToOverview)}
              </Link>
            </div>
          </form>
        ) : null}
      </section>
    </main>
  );
}

function normalizeContentPath(path) {
  if (!path) {
    return '';
  }
  const origin =
    typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  try {
    return new URL(path, origin).pathname;
  } catch {
    return path;
  }
}

function TextField({ label, value, onChange, disabled = false }) {
  return (
    <label className="patent-entry__field">
      <span className="patent-entry__label">{label}</span>
      <input
        className="patent-entry__input"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function DateField({ label, value, onChange, disabled = false }) {
  return (
    <label className="patent-entry__field">
      <span className="patent-entry__label">{label}</span>
      <input
        className="patent-entry__input"
        type="date"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function NumberField({ label, value, onChange, min, step, disabled = false }) {
  return (
    <label className="patent-entry__field">
      <span className="patent-entry__label">{label}</span>
      <input
        className="patent-entry__input"
        type="number"
        min={min}
        step={step}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function TextAreaField({ label, value, onChange, disabled = false }) {
  return (
    <label className="patent-entry__field patent-entry__field--wide">
      <span className="patent-entry__label">{label}</span>
      <textarea
        className="patent-entry__textarea"
        rows="6"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function SelectField({ label, value, onChange, options, disabled = false }) {
  const intl = useIntl();

  return (
    <label className="patent-entry__field">
      <span className="patent-entry__label">{label}</span>
      <select
        className="patent-entry__input"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {formatTokenMessage(intl, option)}
          </option>
        ))}
      </select>
    </label>
  );
}

function normalizeJob(payload) {
  const parent = payload.parent || {};
  return {
    matter_reference_code:
      parent.matter_reference_code ||
      parent.matter_reference ||
      payload.matter_reference_code ||
      '',
    matter_title: parent.title || payload.matter_title || '',
    matter_path: normalizeContentPath(parent['@id'] || payload.matter_path || ''),
    title: payload.title || '',
    job_kind: payload.job_kind || '',
    office_action_path: normalizeContentPath(payload.office_action_path || ''),
    status: payload.status || 'not_started',
    incoming_date: payload.incoming_date || '',
    internal_due_date: payload.internal_due_date || '',
    legal_due_date: payload.legal_due_date || '',
    client_due_date: payload.client_due_date || '',
    legal_due_buffer_days: payload.legal_due_buffer_days ?? 2,
    estimated_hours: payload.estimated_hours ?? 1,
    remaining_hours: payload.remaining_hours ?? payload.estimated_hours ?? '',
    priority: payload.priority || 'medium',
    notes: payload.notes || '',
  };
}

function mergeLegacyOption(options, currentValue) {
  const merged = [...(options || [])];
  if (currentValue && !merged.includes(currentValue)) {
    merged.push(currentValue);
  }
  return merged;
}

function resolveTitleDrivenJobKind(title, options) {
  const trimmed = String(title || '').trim();
  if (!trimmed) {
    return '';
  }
  const normalizedOptions = normalizeOptionList(options);
  if (normalizedOptions.includes(trimmed)) {
    return trimmed;
  }
  const preset = JOB_KIND_PRESETS.find((item) => item.title === trimmed);
  if (!preset) {
    return '';
  }
  return resolvePresetJobKind(preset, options);
}

function resolvePresetJobKind(preset, options) {
  const normalizedOptions = normalizeOptionList(options);
  const preferred = preset?.preferredJobKinds || [];

  for (const candidate of preferred) {
    if (normalizedOptions.includes(candidate)) {
      return candidate;
    }
  }

  const foldedOptions = normalizedOptions.map((option) => ({
    raw: option,
    normalized: normalizeJobKindLabel(option),
  }));

  for (const candidate of preferred) {
    const normalizedCandidate = normalizeJobKindLabel(candidate);
    const matched = foldedOptions.find(
      (option) => option.normalized === normalizedCandidate,
    );
    if (matched) {
      return matched.raw;
    }
  }

  return normalizedOptions[0] || '';
}

function normalizeJobKindLabel(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ');
}

function normalizeOptionList(options) {
  return Array.from(
    new Set(
      (options || []).map((option) => String(option || '').trim()).filter(Boolean),
    ),
  );
}

function isOAWorkspaceCandidate(form) {
  return Boolean(form.office_action_path || form.matter_path);
}

async function extractError(response, intl) {
  try {
    const payload = await response.json();
    return (
      payload.message ||
      payload.error?.message ||
      intl.formatMessage(messages.requestFailedWithStatus, {
        status: response.status,
      })
    );
  } catch {
    return intl.formatMessage(messages.requestFailedWithStatus, {
      status: response.status,
    });
  }
}
